import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Heart, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { getExternalListingHref } from '@/lib/listingContact';
import { formatGender, hasSpecifiedValue } from '@/lib/listingAttributes';

export function ListingCard({ listing, isFavorited: initialFav = false, onFavChange }) {
  const { user } = useAuth();
  const [fav, setFav] = useState(initialFav);
  const [favLoading, setFavLoading] = useState(false);

  const mainImage = listing.images?.[0];
  const imgSrc = mainImage
    ? api.getFileUrl(mainImage)
    : 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400&h=400&fit=crop';
  const externalHref = getExternalListingHref(listing.external_url);
  const sourceLabel = listing.source_name || 'original website';

  const toggleFav = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user || favLoading) return;
    setFavLoading(true);
    try {
      if (fav) {
        await api.removeFavorite(listing.listing_id);
        setFav(false);
      } else {
        await api.addFavorite(listing.listing_id);
        setFav(true);
      }
      onFavChange?.();
    } catch { /* ignore */ }
    setFavLoading(false);
  }, [fav, user, listing.listing_id, favLoading, onFavChange]);

  return (
    <div
      className="listing-card bg-white rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] group"
      data-testid={`listing-card-${listing.listing_id}`}
    >
      <Link to={`/dogs-for-sale/${listing.listing_id}`} className="block cursor-pointer">
        <div className="relative aspect-square overflow-hidden">
          <img
            src={imgSrc}
            alt={listing.title}
            className="listing-card-image w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute top-3 left-3 flex gap-1.5">
            {listing.vaccination_status === 'vaccinated' && (
              <Badge className="bg-[#7ED321] text-white border-0 rounded-full text-xs font-semibold px-2.5 py-0.5">
                Vaccinated
              </Badge>
            )}
            {listing.hypoallergenic === 'yes' && (
              <Badge className="bg-blue-50 text-[#4A90E2] border-0 rounded-full text-xs font-semibold px-2.5 py-0.5 backdrop-blur-sm">
                Hypoallergenic
              </Badge>
            )}
            <Badge className="bg-white/90 text-[#1A1D20] border-0 rounded-full text-xs font-semibold px-2.5 py-0.5 backdrop-blur-sm">
              {listing.size}
            </Badge>
          </div>
          {user && (
            <button
              onClick={toggleFav}
              className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                fav ? 'bg-red-500 text-white' : 'bg-white/80 backdrop-blur-sm text-[#5C636A] hover:bg-white hover:text-red-500'
              }`}
              data-testid={`fav-button-${listing.listing_id}`}
            >
              <Heart className={`w-4 h-4 ${fav ? 'fill-white' : ''}`} />
            </button>
          )}
        </div>
        <div className={`p-5 ${externalHref ? 'pb-3' : ''}`}>
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="font-['Nunito'] font-bold text-[#1A1D20] text-base leading-snug line-clamp-1">
              {listing.title}
            </h3>
            <span className="font-['Nunito'] font-extrabold text-[#F5A623] text-lg whitespace-nowrap">
              ${listing.price?.toLocaleString()}
            </span>
          </div>
          <p className="text-sm text-[#5C636A] mb-2 font-medium">
            {listing.breed} &middot; {listing.age}
            {hasSpecifiedValue(listing.gender) && <> &middot; {formatGender(listing.gender)}</>}
          </p>
          <div className="flex items-center gap-1 text-xs text-[#5C636A]">
            <MapPin className="w-3.5 h-3.5" />
            <span>{listing.location}</span>
          </div>
        </div>
      </Link>
      {externalHref && (
        <div className="px-5 pb-5">
          <a
            href={externalHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-bold text-[#4A90E2] hover:text-[#3a7bc8] hover:underline"
            data-testid={`external-listing-link-${listing.listing_id}`}
          >
            <ExternalLink className="w-3.5 h-3.5" /> View on {sourceLabel}
          </a>
        </div>
      )}
    </div>
  );
}
